
import { Vue, Component, Ref } from 'vue-property-decorator';
import { FormModel } from 'ant-design-vue';
import lazyInject from '@/di';
import RegionService from '@/services/region.service';
import { IResRegion } from '@/common/interface/region';
import SupplyService from '@/services/supply.service';
import { CDistributorList } from '@/models/supply.model';

@Component
export default class EditDistributorModal extends Vue {
  visible = false;

  distributor: any = null;

  regionOptions: IResRegion[] = [];

  parentDistributorOptions: CDistributorList[] = [];

  recordRegions: any[] = [];

  @lazyInject(RegionService)
  protected regionService!: RegionService;

  @lazyInject(SupplyService)
  supplyService!: SupplyService;

  form: any = {
    company_name: '',
    d_level: [] as number[],
    p_sd_code: null,
    province_code: undefined,
    regions: [] as string[],
    contact_name: '',
    contact_phone: '',
    status: [] as number[],
  };

  rules: any = {
    company_name: [
      { required: true, message: '请输入公司全称', trigger: 'blur' },
    ],
    province_code: [
      { required: true, message: '请选择省份', trigger: 'change' },
    ],
    contact_name: [
      { required: true, message: '请输入联系人', trigger: 'blur' },
    ],
    contact_phone: [
      { required: true, message: '请输入联系电话', trigger: 'blur' },
      {
        pattern: /^1[3-9]\d{9}$/,
        message: '请输入正确的联系电话',
        trigger: 'blur',
      },
    ],
    p_sd_code: [
      { required: true, message: '请选择上级经销商', trigger: 'change' },
    ],
  };

  @Ref('ruleForm')
  private ruleForm!: FormModel;

  get isEdit() {
    return !!(this.distributor && this.distributor.id);
  }

  get cityOptions(): IResRegion[] {
    const province_code = this.regionOptions.find(
      (item) => item.id === this.form.province_code,
    );
    return province_code ? province_code.child || [] : [];
  }

  // @Watch('form.province_code')
  onProvinceChange() {
    this.form.regions = [];
    this.onDLevelChange();
  }

  // 切换级别时重置上级经销商：仅地市级(d_level=2)需要选择省级作为上级
  onDLevelChange() {
    this.form.p_sd_code = null;
    this.parentDistributorOptions = [];
    if (this.form.d_level === 2) {
      this.loadParentDistributorOptions();
    }
  }

  // 上级经销商下拉：固定拉取省级(dLevel=1)经销商，支持 company_name 模糊搜索
  async loadParentDistributorOptions() {
    const province = this.regionOptions.find(
      (item) => item.id === this.form.province_code,
    );
    const res: any = await this.supplyService.getDistributorList({
      page: 1,
      pageSize: 999,
      province: province?.name,
      d_level: 1,
    });
    const list = (res && res.list) || [];
    this.parentDistributorOptions = list.map(
      (item: any) => CDistributorList.from(item),
      // eslint-disable-next-line function-paren-newline
    );
  }

  onParentDistributorSearch() {
    this.loadParentDistributorOptions();
  }

  async open(record?: any) {
    this.distributor = record || null;
    this.getRegions();
    if (record) {
      const res = await this.supplyService.getDistributorDetail(record.sdCode);
      const {
        company_name,
        d_level,
        province,
        status,
        contact_name,
        contact_phone,
        sales_region,
      } = res;
      const province_code = this.regionOptions.find(
        (item) => item.name === province,
      );
      const regions = sales_region.map((region: any) => region.city_id);
      this.recordRegions = regions;
      const pSdCode = res.parent_distributor?.sd_code || null;
      this.form = {
        company_name,
        d_level,
        p_sd_code: pSdCode,
        province_code: province_code ? province_code.id : undefined,
        province,
        regions,
        contact_name,
        contact_phone,
        status,
      };
      if (this.form.d_level === 2) {
        this.loadParentDistributorOptions();
      }
    } else {
      this.form = {
        company_name: '',
        d_level: [] as number[],
        p_sd_code: null,
        province_code: undefined,
        regions: [] as string[],
        contact_name: '',
        contact_phone: '',
        status: 1,
      };
      this.parentDistributorOptions = [];
    }
    this.visible = true;
    this.$nextTick(() => {
      this.ruleForm && this.ruleForm.clearValidate('');
    });
  }

  async getRegions() {
    if (this.regionOptions.length) {
      return;
    }
    const res = await this.regionService.allRegions();
    if (res && res.length) {
      this.regionOptions = [...res];
    }
  }

  handleOk() {
    this.ruleForm.validate(async (valid) => {
      if (!valid) {
        return false;
      }
      if (this.isEdit) {
        const toAdd = this.form.regions.filter(
          (id: number) => !this.recordRegions.includes(id),
        );
        const toDel = this.recordRegions.filter(
          (id: number) => !this.form.regions.includes(id),
        );
        const sales_region_add = toAdd.map((cityId: number) => {
          const city = this.cityOptions.find((c) => c.id === cityId);
          return { [cityId]: city ? city.name : '' };
        });
        const sales_region_del = toDel.map((cityId: number) => {
          const province = this.regionOptions.find(
            (p) => p.id === this.form.province_code,
          );
          const city = province
            ? (province.child || []).find((c) => c.id === cityId)
            : undefined;
          return { [cityId]: city ? city.name : '' };
        });
        const params = {
          status: this.form.status,
          contact_name: this.form.contact_name,
          contact_phone: this.form.contact_phone,
          sales_region_add,
          sales_region_del,
        };
        const res = await this.supplyService.postEditDistributor(
          this.distributor.sdCode,
          params,
        );
        if (res) {
          this.$message.success(res);
          this.visible = false;
          this.$emit('refresh');
        }
      } else {
        const province = this.regionOptions.find(
          (item) => item.id === this.form.province_code,
        );
        const sales_region = this.form.regions.map((cityId: number) => {
          const city = this.cityOptions.find((c) => c.id === cityId);
          return { [cityId]: city ? city.name : '' };
        });
        const params = {
          ...this.form,
          province: province ? province.name : '',
          sales_region,
        };
        const res = await this.supplyService.postAddDistributor(params);
        if (res) {
          // this.$message.success(res);
          this.$message.success('新增成功');
          this.visible = false;
          this.$emit('refresh');
        }
      }
    });
  }

  handleCancel() {
    this.visible = false;
    this.ruleForm.resetFields();
  }
}
